<template>
  <div
    ref="root"
    :class="[
      'account-menu',
      `account-menu--overlay-${overlayPosition}`,
      `account-menu--size-${size}`,
      `account-menu--trigger-${triggerLayout}`,
      `account-menu--overlay-align-${overlayAlign}`,
    ]"
    :style="triggerWidth ? { width: `${triggerWidth}px` } : undefined"
  >
    <button
      ref="trigger"
      type="button"
      class="account-menu__trigger"
      :tabindex="triggerTabIndex"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="overlayPanelId"
      :aria-label="ariaLabel"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <Avatar :name="fullName" :imageUrl="avatarImageUrl" :size="avatarSize" class="account-menu__avatar" />
      <div class="account-menu__caption text-caption">
        <span class="account-menu__name text-caption">{{ computedDisplayName }}</span>
        <span v-if="caret" class="account-menu__caret" :class="{ 'account-menu__caret--open': isOpen }" aria-hidden="true">
          <CaretIcon />
        </span>
      </div>
    </button>

    <div
      v-show="isOpen"
      :id="overlayPanelId"
      ref="overlay"
      class="account-menu__overlay"
      role="region"
      :aria-label="menuAriaLabel"
      tabindex="-1"
      @keydown="onOverlayKeydown"
    >
      <div v-if="$slots.header" class="account-menu__overlay-header">
        <slot name="header" />
      </div>
      <MenuLinkList
        v-if="menuItems.length > 0"
        :items="menuItems"
        list-class="list-none p-0 m-0"
        icon-color="#4B4C52"
        @item-click="onItemClick"
      >
        <template v-if="$slots['item-right']" #item-right="{ item }">
          <slot name="item-right" :item="item" />
        </template>
      </MenuLinkList>
      <div v-else class="account-menu__slot">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import type { MenuItem } from '@/components/menu_link_list/MenuLinkListTypes';
import Avatar from '@/components/avatar/Avatar.vue';
import CaretIcon from '@/icons/global_nav/CaretIcon.vue';
import MenuLinkList from '@/components/menu_link_list/MenuLinkList.vue';
import {
  applyDisclosurePanelElementKeydown,
  applyDisclosureTriggerKeydownWhenOpen,
  applyOpenDisclosureDocumentKeydown,
  focusDisclosurePanelStart,
} from '@/components/account_menu/accountMenuKeyboard';

export default {
  name: 'AccountMenu',
  components: { Avatar, CaretIcon, MenuLinkList },
  props: {
    fullName: {
      type: String,
      required: true,
    },
    avatarImageUrl: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    menuItems: {
      type: Array as PropType<MenuItem[]>,
      default: () => [],
    },
    ariaLabel: {
      type: String,
      default: 'Account menu',
    },
    menuAriaLabel: {
      type: String,
      default: 'Account options',
    },
    overlayPosition: {
      type: String,
      default: 'dropdown',
      validator: (value: string) => ['dropdown', 'dropup', 'top', 'bottom', 'right'].includes(value),
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value: string) => ['small', 'medium', 'large'].includes(value),
    },
    triggerWidth: {
      type: Number,
      default: 0,
    },
    caret: {
      type: Boolean,
      default: true,
    },
    triggerLayout: {
      type: String,
      default: 'column',
      validator: (value: string) => ['row', 'column'].includes(value),
    },
    overlayAlign: {
      type: String,
      default: 'right',
      validator: (value: string) => ['left', 'right'].includes(value),
    },
    triggerTabIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isOpen: false,
      overlayPanelId: '',
      documentKeydownHandler: null as ((_ev: KeyboardEvent) => void) | null,
      clickHandler: null as ((_ev: MouseEvent) => void) | null,
    };
  },
  computed: {
    computedDisplayName(): string {
      return this.displayName || this.fullName || 'Account';
    },
    avatarSize(): string {
      const map: Record<string, string> = { small: 'base', medium: 'medium', large: 'large' };
      return map[this.size] ?? 'base';
    },
  },
  watch: {
    isOpen(val: boolean) {
      if (val) {
        this.addListeners();
        this.$nextTick(() => {
          this.focusOverlayContent();
        });
      } else {
        this.removeListeners();
        this.$nextTick(() => {
          (this.$refs.trigger as HTMLElement)?.focus();
        });
      }
    },
  },
  created() {
    this.overlayPanelId = `account-menu-panel-${(this as any)._uid}`;
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
    },
    removeListeners() {
      if (this.documentKeydownHandler) {
        document.removeEventListener('keydown', this.documentKeydownHandler, true);
        this.documentKeydownHandler = null;
      }
      if (this.clickHandler) {
        document.removeEventListener('click', this.clickHandler, true);
        this.clickHandler = null;
      }
    },
    focusOverlayContent() {
      const overlay = this.$refs.overlay as HTMLElement | undefined;
      if (overlay) focusDisclosurePanelStart(overlay);
    },
    onOverlayKeydown(event: KeyboardEvent) {
      applyDisclosurePanelElementKeydown(event, () => this.close());
    },
    onTriggerKeydown(event: KeyboardEvent) {
      if (!this.isOpen) return;
      applyDisclosureTriggerKeydownWhenOpen(event, this.$refs.overlay as HTMLElement | undefined, () => this.close());
    },
    onItemClick() {
      this.close();
    },
    addListeners() {
      this.documentKeydownHandler = (event: KeyboardEvent) => {
        applyOpenDisclosureDocumentKeydown(event, {
          panel: this.$refs.overlay as HTMLElement | undefined,
          trigger: this.$refs.trigger as HTMLElement | undefined,
          close: () => this.close(),
        });
      };
      this.clickHandler = (event: MouseEvent) => {
        const target = event.target as Node;
        const root = this.$refs.root as HTMLElement;
        if (root && !root.contains(target)) {
          this.close();
        }
      };
      document.addEventListener('keydown', this.documentKeydownHandler, true);
      document.addEventListener('click', this.clickHandler, true);
    },
  },
};
</script>

<style lang="scss" scoped>
.account-menu {
  position: relative;
  display: inline-block;
  height: 100%;
}

.account-menu__trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 0;
  border: 2px solid var(--light-surface-foreground, #fff);
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:hover {
    border-color: #f3f3f3;
    background-color: #f3f3f3;

    .account-menu__caption {
      color: #bb0049;
    }

    .account-menu__caret ::v-deep path {
      fill: #bb0049;
    }
  }

  &:focus {
    outline: none;
    border: 2px dotted #bb0049;
  }
}

.account-menu--trigger-row .account-menu__trigger {
  flex-direction: row;
  justify-content: flex-start;
  gap: 6px;
}

.account-menu__avatar {
  flex-shrink: 0;
}

.account-menu__caption {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 2px;
  margin-bottom: 0;

  @media (max-width: 767px) {
    justify-content: flex-start;
    margin-bottom: 0;
  }
}

.account-menu--trigger-row .account-menu__caption {
  justify-content: flex-start;
}

.account-menu__name {
  max-width: 64px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-menu__caret {
  flex-shrink: 0;
  display: inline-flex;
  transition: transform 0.2s ease;
  margin-bottom: 0;

  &--open {
    transform: rotate(180deg);
  }

  @media (max-width: 767px) {
    margin-bottom: 0;
  }
}

.account-menu__overlay {
  position: absolute;
  top: 100%;
  right: 0;
  left: auto;
  margin-top: 1px;
  min-width: 270px;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(5, 7, 15, 0.12);
  z-index: 100;

  &:focus {
    outline: none;
  }
}

.account-menu--overlay-dropdown.account-menu--overlay-align-left .account-menu__overlay,
.account-menu--overlay-dropup.account-menu--overlay-align-left .account-menu__overlay {
  right: auto;
  left: 0;
}

.account-menu--overlay-dropup .account-menu__overlay {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 4px;
}

.account-menu--overlay-top .account-menu__overlay {
  position: fixed;
  top: var(--nav-height, 48px);
  right: 0;
  left: 0;
  bottom: auto;
  width: auto;
  height: auto;
  margin: 0;
  min-width: unset;
  border-radius: 4px;
  padding: 16px;
  overflow: auto;
  z-index: 101;
}

.account-menu--overlay-bottom .account-menu__overlay {
  position: fixed;
  right: 0;
  left: 0;
  bottom: 80px;
  top: auto;
  width: auto;
  height: auto;
  max-height: calc(100vh - var(--nav-height, 48px) - 80px);
  margin: 0;
  min-width: unset;
  border-radius: 4px;
  padding: 16px;
  overflow: auto;
  z-index: 101;
}

.account-menu--overlay-right .account-menu__overlay {
  position: fixed;
  top: calc(var(--nav-height, 48px) + 1px);
  right: 0;
  left: auto;
  bottom: auto;
  width: min(270px, 100vw);
  max-height: calc(100vh - var(--nav-height, 48px) - 1px - 16px);
  height: auto;
  margin: 0;
  border-radius: 4px 0 0 4px;
  box-shadow: -2px 0 12px rgba(5, 7, 15, 0.12);
  overflow: auto;
  z-index: 101;
}

.account-menu__overlay-header {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d7d8d9;
}

.account-menu__slot {
  padding: 0;
}
</style>
